/**
 * EULA acceptance API client (DEV-906).
 *
 * Wraps POST /api/auth/accept-eula. The endpoint is authenticated —
 * apiFetch handles the bearer token automatically. Server validates
 * the requested version against EulaConstants.CURRENT_VERSION and
 * rejects mismatches.
 */

import { apiFetch } from './client';
import { invalidateAndRefetch, QUERY_KEYS } from './queryCache';
import { invalidateOnboardingState } from './onboarding';

export interface AcceptEulaResponse {
  eulaAcceptedAt: string;
  eulaVersionAccepted: number;
}

/**
 * Submit the user's acceptance of the given EULA version. Server
 * pairs the requested version with `now()` and persists both.
 *
 * Side effect: invalidates the security users cache and triggers a
 * refetch so `useApiSecurityData` picks up the new
 * eula_accepted_at + eula_version_accepted values, and the synthetic
 * milestone evaluator clears the accept_eula task on next render.
 *
 * NOTE — must use `invalidateAndRefetch`, NOT a raw
 * `dispatchEvent(CACHE_UPDATE_EVENT)`. Per the queryCache module's
 * comment, only `invalidateAndRefetch` is allowed to fire that event,
 * because it ALSO marks the cache entry stale (fetchedAt = 0). Without
 * the staleness mark, subscribers receive the event but `fetchForKey`
 * returns the cached value because the entry still looks fresh. The
 * task would never clear without a manual reload.
 *
 * Throws ApiError on rejection (e.g., version mismatch, expired token).
 */
export async function acceptEula(version: number): Promise<AcceptEulaResponse> {
  const result = await apiFetch<AcceptEulaResponse>('/api/auth/accept-eula', {
    method: 'POST',
    body: JSON.stringify({ version }),
  });

  // Mark security cache stale + notify subscribers → useApiSecurityData
  // refetches the user row including the updated EULA acceptance state.
  invalidateAndRefetch(QUERY_KEYS.security);
  // EULA currency is a rung basic — this accept can complete the
  // Explorer→Contributor promotion; App.tsx's onboarding instance must
  // hear about it live so the celebration popup fires (2026-07-08).
  invalidateOnboardingState();

  return result;
}
